import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
  collection,
  getDocs,
  query,
  where,
  orderBy
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { parseISO, format, isSunday, differenceInCalendarDays } from "date-fns";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";
import CustomDropdown from "../CustomDropdown";
import { useAuth } from "../../hooks/useAuth";
import { getLeaveApplyPermissions } from "../../utils/permissions";
import { showToast } from "../../utils/toastUtils";
import {
  fetchLeaveEntries,
  fetchAllLeaveEntries,
  fetchLeaveLimits,
  submitLeaveEntries,
  deleteLeaveEntry,
} from "../../utils/leaveUtils";
import {
  enumerateDateRange,
  computeNumDaysFromPortions,
  formatNumDays,
  describeDayPortions,
  DAY_PORTIONS,
  DAY_PORTION_LABEL,
} from "../../utils/leaveUtils";
import type { LeaveEntry, DayPortion } from "../../utils/leaveUtils";
import { fetchCheckInRecordsForYear } from "../../utils/checkInOutUtils";
import {
  computeAccruedLeaveLimits,
  applyAddons,
  fetchLeaveAccrualConfig,
  DEFAULT_ACCRUAL_CONFIG,
  EMPTY_ACCRUAL,
  EMPTY_ADDONS,
  USE_CHECKIN_BASED_ACCRUAL,
} from "../../utils/leaveAccrualUtils";
import type {
  LeaveAccrual,
  LeaveLimitAddons,
  LeaveAccrualConfig,
} from "../../utils/leaveAccrualUtils";

// Inline SVG icons for actions
const EditSvg: React.FC<{ size?: number; className?: string }> = ({
  size = 16,
  className = "",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const TrashSvg: React.FC<{ size?: number; className?: string }> = ({
  size = 16,
  className = "",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

/**
 * The current calendar year, re-checked hourly so a session left open across
 * midnight on 31 December rolls over to the new year (and a fresh, zeroed
 * accrual) without needing a page refresh.
 */
const useCurrentYear = (): number => {
  const [year, setYear] = useState(() => new Date().getFullYear());

  useEffect(() => {
    const id = setInterval(() => {
      const now = new Date().getFullYear();
      setYear((prev) => (prev === now ? prev : now));
    }, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return year;
};

/**
 * Per-day duration picker. Every date in the selected range gets its own
 * Full Day / First Half / Second Half choice, defaulting to Full Day.
 */
const DayPortionPicker: React.FC<{
  dates: string[];
  portions: Record<string, DayPortion>;
  onChange: (date: string, portion: DayPortion) => void;
  isDark: boolean;
}> = ({ dates, portions, onChange, isDark }) => {
  if (dates.length === 0) {
    return (
      <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
        Choose a From and To date to set the duration for each day.
      </p>
    );
  }

  return (
    <div
      className={`max-h-36 overflow-y-auto rounded-md border divide-y ${
        isDark
          ? "border-[#2E2F33] divide-[#2E2F33] bg-[#0B0B0F]"
          : "border-gray-200 divide-gray-100 bg-white"
      }`}
    >
      {dates.map((date) => {
        const selected = portions[date] || "full";
        const weekday = new Date(date).toLocaleDateString(undefined, {
          weekday: "short",
        });
        return (
          <div
            key={date}
            className="flex h-12 items-center justify-between gap-3 px-3 shrink-0"
          >
            <span
              className={`text-xs font-semibold whitespace-nowrap ${
                isDark ? "text-gray-200" : "text-gray-700"
              }`}
            >
              {formatDateDDMMYYYY(date)}
              <span
                className={`ml-1.5 font-normal ${
                  isDark ? "text-gray-500" : "text-gray-400"
                }`}
              >
                {weekday}
              </span>
            </span>
            <div
              className={`inline-flex rounded-full p-0.5 border ${
                isDark
                  ? "border-[#2E2F33] bg-[#14161B]"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              {DAY_PORTIONS.map((portion) => {
                const isActive = selected === portion;
                return (
                  <button
                    key={portion}
                    type="button"
                    onClick={() => onChange(date, portion)}
                    aria-pressed={isActive}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all whitespace-nowrap focus:outline-none ${
                      isActive
                        ? isDark
                          ? "bg-[#3A3D44] text-white shadow-sm"
                          : "bg-gray-300 text-gray-900 shadow-sm"
                        : isDark
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    {DAY_PORTION_LABEL[portion]}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Day count for a leave row. The number already conveys a half day (1.5), so
 * no badge — hovering reveals which day is a first/second half.
 */
const DaysCell: React.FC<{ entry: LeaveEntry }> = ({ entry }) => (
  <span title={describeDayPortions(entry) || undefined}>
    {formatNumDays(entry.numDays ?? 0)}
  </span>
);

type Member = {
  id: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  tempLeaveRequests?: LeaveEntry[];
  dateOfJoining?: string;
  joiningDate?: string;
};

type Manager = {
  id: string;
  firstName?: string;
  lastName?: string;
  role?: string;
};

interface LeaveApplyProps {
  organizationId?: string;
  userId?: string;
  moduleClicked?: string;
  currentUserRole?: string;
  currentUser?: any;
}

const normalizeRole = (raw?: string | null) =>
  (raw || "").toLowerCase().replace(/\s+/g, "");
const isManagerialRole = (raw?: string | null) => {
  const norm = normalizeRole(raw);
  return norm === "manager" || norm === "managingdirector";
};

const LeaveApply: React.FC<LeaveApplyProps> = ({
  organizationId,
  userId,
  moduleClicked,
  currentUserRole,
  currentUser,
}) => {
  const { user: authUser } = useAuth();
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // Compute leave permissions
  const leavePerms = React.useMemo(
    () => getLeaveApplyPermissions(currentUser),
    [currentUser]
  );
  const [paidLimit, setPaidLimit] = useState<number>(8);
  const [sickLimit, setSickLimit] = useState<number>(4);
  const [casualLimit, setCasualLimit] = useState<number>(4);

  // Default leave limits when no limits are found in Firebase
  const DEFAULT_LEAVE_LIMITS = {
    sick: 12,
    casual: 12,
    paid: 24,
  };

  // State for leave limits from LeaveManagement
  const [leaveLimits, setLeaveLimits] = useState<{
    sick: number;
    casual: number;
    paid: number;
  }>({
    sick: 0,
    casual: 0,
    paid: 0,
  });

  // State for taken leave statistics from LeaveManagement
  const [leaveStats, setLeaveStats] = useState<{
    sickTaken: number;
    casualTaken: number;
    paidTaken: number;
    unpaidTaken: number;
    totalTaken: number;
  }>({
    sickTaken: 0,
    casualTaken: 0,
    paidTaken: 0,
    unpaidTaken: 0,
    totalTaken: 0,
  });

  const [leaveEntries, setLeaveEntries] = useState<LeaveEntry[]>([]);
  const [rejectedLeaves, setRejectedLeaves] = useState<
    (LeaveEntry & { id: string })[]
  >([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [roleFromOrg, setRoleFromOrg] = useState<string>("");

  const [selectedTab, setSelectedTab] = useState<"new" | "rejected" | "history">("new");
  const [isModalVisible, setModalVisible] = useState(false);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [leaveType, setLeaveType] = useState("");
  const [reason, setReason] = useState("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [numDays, setNumDays] = useState<string>("");
  const [selectedManager, setSelectedManager] = useState<string>("");

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editSource, setEditSource] = useState<"draft" | "rejected" | "history">("rejected");
  const [editLeaveType, setEditLeaveType] = useState("");
  const [editReason, setEditReason] = useState("");
  const [editFromDate, setEditFromDate] = useState("");
  const [editToDate, setEditToDate] = useState("");
  const [editNumDays, setEditNumDays] = useState("");
  const [editSelectedManager, setEditSelectedManager] = useState("");

  // Joining date and accrual state (must be declared before useMemo hooks)
  const [joiningDate, setJoiningDate] = useState<string | null>(null);
  // Tracks that the joining-date lookup has finished, so the accrual doesn't
  // run against a not-yet-loaded null and then immediately recompute.
  const [joiningDateResolved, setJoiningDateResolved] = useState(false);
  const [approvedPaidDaysCurrentYear, setApprovedPaidDaysCurrentYear] = useState(0);
  const [approvedSickDaysCurrentYear, setApprovedSickDaysCurrentYear] = useState(0);
  const [approvedCasualDaysCurrentYear, setApprovedCasualDaysCurrentYear] = useState(0);
  const [totalConsideredPaidDaysCurrentYear, setTotalConsideredPaidDaysCurrentYear] = useState(0);
  const [totalConsideredSickDaysCurrentYear, setTotalConsideredSickDaysCurrentYear] = useState(0);
  const [totalConsideredCasualDaysCurrentYear, setTotalConsideredCasualDaysCurrentYear] = useState(0);
  const [historyEntries, setHistoryEntries] = useState<LeaveEntry[]>([]);
  const [pendingSickDaysCurrentYear, setPendingSickDaysCurrentYear] = useState(0);
  const [pendingCasualDaysCurrentYear, setPendingCasualDaysCurrentYear] = useState(0);
  const [pendingPaidDaysCurrentYear, setPendingPaidDaysCurrentYear] = useState(0);
  const [pendingUnpaidDaysCurrentYear, setPendingUnpaidDaysCurrentYear] = useState(0);
  const [pendingCompOffDaysCurrentYear, setPendingCompOffDaysCurrentYear] = useState(0);
  const [compOffAvailable, setCompOffAvailable] = useState<number>(0);
  const [compOffLoading, setCompOffLoading] = useState<boolean>(false);
  const [limitsLoading, setLimitsLoading] = useState<boolean>(false);
  const [compOffTaken, setCompOffTaken] = useState<number>(0);
  const [compOffEnabled, setCompOffEnabled] = useState<boolean>(false);
  const compOffRemaining = Math.max(compOffAvailable - compOffTaken, 0);

  // Check-in based accrual (26 working days -> 1 casual, 30 -> 1 sick).
  // Scoped to the current calendar year, so limits reset to 0 every January.
  const accrualYear = useCurrentYear();
  const [accrual, setAccrual] = useState<LeaveAccrual>(EMPTY_ACCRUAL);
  const [accrualLoading, setAccrualLoading] = useState<boolean>(false);
  // Extra days granted by an admin in LeaveManagement → Configuration.
  const [leaveAddons, setLeaveAddons] = useState<LeaveLimitAddons>(EMPTY_ADDONS);
  // Org-wide accrual thresholds, set by an admin in LeaveManagement.
  const [accrualConfig, setAccrualConfig] = useState<LeaveAccrualConfig>(DEFAULT_ACCRUAL_CONFIG);
  const [accrualConfigLoaded, setAccrualConfigLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchLeaveAccrualConfig(organizationId).then((cfg) => {
      if (cancelled) return;
      setAccrualConfig(cfg);
      setAccrualConfigLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);
  // The summary cards depend on both the stored limits and the check-in accrual.
  const summaryLoading = limitsLoading || accrualLoading;

  // Per-day First Half / Second Half / Full Day selection for the add + edit forms
  const [dayPortions, setDayPortions] = useState<Record<string, DayPortion>>({});
  const [editDayPortions, setEditDayPortions] = useState<Record<string, DayPortion>>({});

  const addRangeDates = React.useMemo(
    () => enumerateDateRange(fromDate, toDate),
    [fromDate, toDate]
  );
  const editRangeDates = React.useMemo(
    () => enumerateDateRange(editFromDate, editToDate),
    [editFromDate, editToDate]
  );

  const getManagerName = (mgrId?: string) => {
    if (!mgrId) return "-";
    const m = managers.find((mm) => mm.id === mgrId);
    return m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : mgrId;
  };

  // Compute joining date validity and lockout
  const parsedJoiningDate = React.useMemo(() => {
    if (!joiningDate) {
      return new Date(new Date().getFullYear(), 0, 1);
    }
    // Handle Firebase Timestamp or String
    let dateObj: Date;
    if (typeof joiningDate === 'object' && (joiningDate as any).seconds) {
      dateObj = new Date((joiningDate as any).seconds * 1000);
    } else {
      dateObj = new Date(joiningDate);
    }
    
    if (isNaN(dateObj.getTime())) {
      return new Date(new Date().getFullYear(), 0, 1);
    }
    return dateObj;
  }, [joiningDate]);

  const daysSinceJoining = React.useMemo(() => {
    if (!parsedJoiningDate) return null;
    return differenceInCalendarDays(new Date(), parsedJoiningDate);
  }, [parsedJoiningDate]);

  const isLockout = React.useMemo(() => {
    if (!parsedJoiningDate || daysSinceJoining === null) return false;
    return daysSinceJoining < 20;
  }, [parsedJoiningDate, daysSinceJoining]);

  // Effective totals.
  //
  // TEMPORARY: while USE_CHECKIN_BASED_ACCRUAL is on, the limits come from the
  // check-in accrual (1 casual per 26 working days, 1 sick per 30 working days
  // since joining) and take precedence over admin-configured limits. Flip the
  // flag in leaveAccrualUtils to restore the previous behaviour, where
  // admin-configured limits win and monthly accrual is the fallback.
  const effectiveTotals = React.useMemo(() => {
    if (USE_CHECKIN_BASED_ACCRUAL) {
      // Accrued days plus whatever an admin granted on top in Configuration.
      return applyAddons(accrual, leaveAddons);
    }

    if (leaveLimits.paid > 0 || leaveLimits.sick > 0 || leaveLimits.casual > 0) {
      return leaveLimits;
    }

    const today = new Date();
    const realYear = today.getFullYear();
    const realMonth = today.getMonth();

    const startOfYearDate = new Date(realYear, 0, 1);
    const accrualStart = parsedJoiningDate > startOfYearDate ? parsedJoiningDate : startOfYearDate;

    let sickAccrual = 0;
    let casualAccrual = 0;

    // Accrue months from start of year up to current month
    for (let m = 0; m <= realMonth; m++) {
      if (m >= accrualStart.getMonth()) {
        sickAccrual += 1;
        casualAccrual += 1;
      }
    }

    return {
      paid: sickAccrual + casualAccrual,
      sick: sickAccrual,
      casual: casualAccrual,
    };
  }, [parsedJoiningDate, leaveLimits, accrual, leaveAddons]);

  // Recompute the check-in accrual whenever the member or their joining date changes.
  useEffect(() => {
    if (!USE_CHECKIN_BASED_ACCRUAL) return;
    if (!organizationId || !userId) {
      setAccrual(EMPTY_ACCRUAL);
      return;
    }
    // Wait for the lookup, but don't require a value — accounts created before
    // joiningDate existed (notably the founding admin) simply accrue from 1 Jan.
    if (!joiningDateResolved) return;
    // Thresholds drive the result, so don't compute against the defaults first.
    if (!accrualConfigLoaded) return;

    let cancelled = false;
    setAccrualLoading(true);
    computeAccruedLeaveLimits(organizationId, userId, joiningDate, {
      year: accrualYear,
      config: accrualConfig,
    })
      .then((result) => {
        if (!cancelled) setAccrual(result);
      })
      .finally(() => {
        if (!cancelled) setAccrualLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, userId, joiningDate, joiningDateResolved, accrualYear, accrualConfig, accrualConfigLoaded]);

  // Derived remaining counts for Paid categories (using accruals when available)
  const sickRemainingTotal = React.useMemo(() => {
    return Math.max(0, effectiveTotals.sick - totalConsideredSickDaysCurrentYear);
  }, [effectiveTotals.sick, totalConsideredSickDaysCurrentYear]);

  const casualRemainingTotal = React.useMemo(() => {
    return Math.max(0, effectiveTotals.casual - totalConsideredCasualDaysCurrentYear);
  }, [effectiveTotals.casual, totalConsideredCasualDaysCurrentYear]);

  const sickRemainingApproved = React.useMemo(() => {
    return Math.max(0, effectiveTotals.sick - approvedSickDaysCurrentYear - pendingSickDaysCurrentYear);
  }, [effectiveTotals.sick, approvedSickDaysCurrentYear, pendingSickDaysCurrentYear]);

  const casualRemainingApproved = React.useMemo(() => {
    return Math.max(0, effectiveTotals.casual - approvedCasualDaysCurrentYear - pendingCasualDaysCurrentYear);
  }, [effectiveTotals.casual, approvedCasualDaysCurrentYear, pendingCasualDaysCurrentYear]);

  // Helpers for remaining by reason
  const getRemainingByReason = (rs?: string, useTotal = false) => {
    if (!rs) return 0;
    if (rs === "Sick") return useTotal ? sickRemainingTotal : sickRemainingApproved;
    if (rs === "Casual") return useTotal ? casualRemainingTotal : casualRemainingApproved;
    if (rs === "Comp Off" || rs === "CompOff" || rs === "Comp-Off") return compOffRemaining;
    return 0;
  };

  const addNumDaysWarning = React.useMemo(() => {
    if (!reason || !numDays) return "";
    const r = reason.toLowerCase();
    const lType = leaveType.toLowerCase();
    if (lType === "unpaid") return "";

    let limit = 0;
    let taken = 0;
    let pending = 0;

    if (r === "sick") {
      limit = effectiveTotals.sick;
      taken = approvedSickDaysCurrentYear;
      pending = pendingSickDaysCurrentYear;
    } else if (r === "casual") {
      limit = effectiveTotals.casual;
      taken = approvedCasualDaysCurrentYear;
      pending = pendingCasualDaysCurrentYear;
    } else if (r === "comp off" || r === "compoff" || r === "comp-off") {
      limit = compOffAvailable;
      taken = compOffTaken;
      pending = pendingCompOffDaysCurrentYear;
    } else {
      limit = effectiveTotals.paid;
      taken = approvedPaidDaysCurrentYear;
      pending = pendingPaidDaysCurrentYear;
    }

    const totalUsed = taken + pending;
    const canApplyFor = Math.max(0, limit - totalUsed);

    if (Number(numDays) > canApplyFor) {
      return `You can only apply for ${canApplyFor} more day${canApplyFor === 1 ? "" : "s"} of this leave type.`;
    }
    return "";
  }, [
    reason,
    numDays,
    leaveType,
    leaveLimits,
    effectiveTotals,
    parsedJoiningDate,
    approvedSickDaysCurrentYear,
    pendingSickDaysCurrentYear,
    approvedCasualDaysCurrentYear,
    pendingCasualDaysCurrentYear,
    approvedPaidDaysCurrentYear,
    pendingPaidDaysCurrentYear,
    compOffAvailable,
    compOffTaken,
    pendingCompOffDaysCurrentYear
  ]);

  const editNumDaysWarning = React.useMemo(() => {
    if (!editReason || !editNumDays) return "";
    const r = editReason.toLowerCase();
    const lType = editLeaveType.toLowerCase();
    if (lType === "unpaid") return "";

    let limit = 0;
    let taken = 0;
    let pending = 0;

    if (r === "sick") {
      limit = effectiveTotals.sick;
      taken = approvedSickDaysCurrentYear;
      pending = pendingSickDaysCurrentYear;
    } else if (r === "casual") {
      limit = effectiveTotals.casual;
      taken = approvedCasualDaysCurrentYear;
      pending = pendingCasualDaysCurrentYear;
    } else if (r === "comp off" || r === "compoff" || r === "comp-off") {
      limit = compOffAvailable;
      taken = compOffTaken;
      pending = pendingCompOffDaysCurrentYear;
    } else {
      limit = effectiveTotals.paid;
      taken = approvedPaidDaysCurrentYear;
      pending = pendingPaidDaysCurrentYear;
    }

    const totalUsed = taken + pending;
    const currentDays = Number(editNumDays) || 0;
    // When editing, we should know if 'this' leave is accounted for in 'pending'
    // Actually calculateLeaveSummary counts [drafts + historyApproved]
    // To be precise, we'd subtract editNumDays from totalUsed if this entry exists in Drafts.
    const canApplyFor = Math.max(0, limit - (totalUsed - currentDays));

    if (currentDays > canApplyFor) {
      return `You can only apply for ${canApplyFor} more day${canApplyFor === 1 ? "" : "s"} of this leave type.`;
    }
    return "";
  }, [
    editReason,
    editNumDays,
    editLeaveType,
    leaveLimits,
    effectiveTotals,
    parsedJoiningDate,
    approvedSickDaysCurrentYear,
    pendingSickDaysCurrentYear,
    approvedCasualDaysCurrentYear,
    pendingCasualDaysCurrentYear,
    approvedPaidDaysCurrentYear,
    pendingPaidDaysCurrentYear,
    compOffAvailable,
    compOffTaken,
    pendingCompOffDaysCurrentYear
  ]);

  // Compute comp-off availability from Sunday/holiday check-ins (current year)
  const computeCompOffAvailability = async (
    organizationId?: string,
    userId?: string
  ) => {
    if (!organizationId || !userId) return;
    try {
      setCompOffLoading(true);
      const currentYear = new Date().getFullYear();
      // Holidays map
      const holidaysCol = collection(
        db,
        "organization",
        organizationId,
        "holidays"
      );
      const holidaysSnap = await getDocs(holidaysCol);
      const holidaySet = new Set<string>();
      holidaysSnap.forEach((d) => {
        const data: any = d.data();
        if (data?.date) {
          const key = typeof data.date === "string" ? data.date : String(data.date);
          holidaySet.add(key);
        }
      });

      const checksSnap = await fetchCheckInRecordsForYear(organizationId, userId, currentYear);

      const dates = checksSnap
        .map((d: any) => d.checkInTime as string)
        .filter(Boolean)
        .map((s) => {
          try {
            return parseISO(s);
          } catch {
            return null;
          }
        })
        .filter((dt): dt is Date => dt instanceof Date && !isNaN(dt.getTime()));

      // Unique by date within current year
      const dateKeys = new Set<string>();
      dates.forEach((dt) => {
        if (dt.getFullYear() === currentYear) {
          dateKeys.add(format(dt, "yyyy-MM-dd"));
        }
      });

      let count = 0;
      dateKeys.forEach((key) => {
        const dt = parseISO(key);
        if (isNaN(dt.getTime())) return;
        if (isSunday(dt) || holidaySet.has(key)) count += 1;
      });

      setCompOffAvailable(count);
    } catch (err) {
      console.error("Failed to compute comp-off availability", err);
      setCompOffAvailable(0);
    }
    finally {
      setCompOffLoading(false);
    }
  };

  // Fetch leave limits and statistics from LeaveManagement component's Firebase path
  const fetchLeaveData = async (drafts?: LeaveEntry[]) => {
    setLimitsLoading(true);
    if (!organizationId || !userId) return;

    try {
      const currentYear = new Date().getFullYear();

      const [limits, entries] = await Promise.all([
        fetchLeaveLimits(organizationId, userId),
        fetchLeaveEntries(organizationId, userId, currentYear),
      ]);

      // Stored limits only count for the year they were saved for; once a new
      // year begins they are stale and the member starts again from 0.
      // (limitYear is absent on legacy docs — those are still honoured.)
      const limitsAreForThisYear =
        !!limits &&
        (limits.limitYear === undefined || limits.limitYear === currentYear);

      if (limits && limitsAreForThisYear) {
        setLeaveLimits({ sick: limits.sickLimit, casual: limits.casualLimit, paid: limits.paidLimit });
      } else {
        // No custom limits for this year — let effectiveTotals fall back to accrual.
        setLeaveLimits({ sick: 0, casual: 0, paid: 0 });
      }

      // Add-ons expire per year too — a bonus granted in one year does not
      // carry into the next.
      const addonsAreForThisYear =
        !!limits &&
        (limits.addonYear === undefined || limits.addonYear === currentYear);
      setLeaveAddons(
        limits && addonsAreForThisYear
          ? {
              paid: limits.paidAddon || 0,
              sick: limits.sickAddon || 0,
              casual: limits.casualAddon || 0,
            }
          : EMPTY_ADDONS
      );

      if (entries.length > 0) {
        const allEntries = entries
          .map((e: any) => ({
            ...e,
            numDays: String(e.numDays || 0),
          }))
          .sort((a: any, b: any) => {
            const dateA = new Date(a.fromDate).getTime();
            const dateB = new Date(b.fromDate).getTime();
            if (dateA !== dateB) return dateB - dateA;
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          });
        setHistoryEntries(allEntries);

        const approved = entries
          .filter((e: any) => e.approvalStatus === "Approved" || e.approvalStatus === "approved")
          .map((e: any) => ({
            ...e,
            numDays: Number(e.numDays),
          }));

        // Calculate statistics for current year
        const yearReqs = approved.filter((r: any) => {
          const d = new Date(r.fromDate);
          return d.getFullYear() === currentYear;
        });

        const stats = {
          sickTaken: yearReqs
            .filter((r: any) => r.reason === "Sick")
            .reduce((s: number, r: any) => s + r.numDays, 0),
          casualTaken: yearReqs
            .filter((r: any) => r.reason === "Casual")
            .reduce((s: number, r: any) => s + r.numDays, 0),
          paidTaken: yearReqs
            .filter((r: any) => r.leaveType === "Paid")
            .reduce((s: number, r: any) => s + r.numDays, 0),
          unpaidTaken: yearReqs
            .filter((r: any) => r.leaveType === "Unpaid")
            .reduce((s: number, r: any) => s + r.numDays, 0),
          totalTaken: yearReqs.reduce((s: number, r: any) => s + r.numDays, 0),
        };

        // Comp Off taken (approved, current year)
        const compTaken = approved
          .filter((r: any) => {
            const d = new Date(r.fromDate);
            return (
              r.leaveType === "Paid" &&
              (r.reason === "Comp Off" || r.reason === "CompOff" || r.reason === "Comp-Off") &&
              d.getFullYear() === currentYear
            );
          })
          .reduce((s: number, r: any) => s + (Number(r.numDays) || 0), 0);

        setCompOffTaken(compTaken);
        setLeaveStats(stats);
        calculateLeaveSummary(allEntries, drafts || leaveEntries);
      } else {
        setHistoryEntries([]);
        setLeaveStats({
          sickTaken: 0,
          casualTaken: 0,
          paidTaken: 0,
          unpaidTaken: 0,
          totalTaken: 0,
        });
        setCompOffTaken(0);
      }
    } catch (error) {
      console.error("Error fetching leave data:", error);
      // Ensure default limits are used on error
      setLeaveLimits(DEFAULT_LEAVE_LIMITS);
      setLeaveStats({
        sickTaken: 0,
        casualTaken: 0,
        paidTaken: 0,
        unpaidTaken: 0,
        totalTaken: 0,
      });
      setCompOffTaken(0);
    }
    finally {
      setLimitsLoading(false);
    }
  };

  // Fetch joining date from organization members
  useEffect(() => {
    if (!organizationId || !userId) return;
    const fetchJoiningDate = async () => {
      try {
        const orgSnap = await getDoc(doc(db, `organization/${organizationId}`));
        if (orgSnap.exists()) {
          const { members = [] } = orgSnap.data();
          const currentMember = members.find((m: any) => m.id === userId);
          const memberJoining =
            currentMember?.dateOfJoining || currentMember?.joiningDate || null;
          setJoiningDate(memberJoining || null);
        } else {
          setJoiningDate(null);
        }
      } catch (err) {
        console.error("Error fetching joining date:", err);
        setJoiningDate(null);
      } finally {
        setJoiningDateResolved(true);
      }
    };
    fetchJoiningDate();
  }, [organizationId, userId]);

  const calculateLeaveSummary = (history: LeaveEntry[], drafts: LeaveEntry[]) => {
    const currentYear = new Date().getFullYear();

    const consideredHistory = history.filter((entry: LeaveEntry) => {
      const status = (entry.approvalStatus || "").toLowerCase();
      const isRejected = status === "rejected" || status === "reject";
      const fromDateStr = entry.fromDate;
      if (!fromDateStr) return false;
      const fromD = new Date(fromDateStr);
      return !isRejected && fromD.getFullYear() === currentYear;
    });

    const consideredDrafts = drafts.filter((entry: LeaveEntry) => {
      const fromDateStr = entry.fromDate;
      if (!fromDateStr) return false;
      const fromD = new Date(fromDateStr);
      return fromD.getFullYear() === currentYear;
    });

    const allConsidered = [...consideredHistory, ...consideredDrafts];

    let sickApproved = 0;
    let casualApproved = 0;
    let paidApproved = 0;
    let sickTotal = 0;
    let casualTotal = 0;
    let paidTotal = 0;
    let sickPending = 0;
    let casualPending = 0;
    let paidPending = 0;
    let compOffPending = 0;
    let pendingUnpaidTotal = 0;

    allConsidered.forEach((entry: LeaveEntry) => {
      const days = Number(entry.numDays) || 0;
      const reason = (entry.reason || "").toLowerCase();
      const leaveType = (entry.leaveType || "").toLowerCase();
      const status = (entry.approvalStatus || "").toLowerCase();
      const isApproved = status === "approved";
      const isCompOff = reason === "comp off" || reason === "compoff" || reason === "comp-off";

      if (!isCompOff) {
        if (reason === "sick") sickTotal += days;
        if (reason === "casual") casualTotal += days;
        if (leaveType === "paid") paidTotal += days;
      }

      if (leaveType === "unpaid" && !isApproved) pendingUnpaidTotal += days;

      if (isApproved) {
        if (!isCompOff) {
          if (reason === "sick") sickApproved += days;
          if (reason === "casual") casualApproved += days;
          if (leaveType === "paid") paidApproved += days;
        }
      } else {
        if (isCompOff) {
          compOffPending += days;
        } else {
          if (reason === "sick") sickPending += days;
          if (reason === "casual") casualPending += days;
          if (leaveType === "paid") paidPending += days;
        }
      }
    });

    setPendingSickDaysCurrentYear(sickPending);
    setPendingCasualDaysCurrentYear(casualPending);
    setPendingPaidDaysCurrentYear(Math.max(paidPending, sickPending + casualPending));
    setPendingUnpaidDaysCurrentYear(pendingUnpaidTotal);
    setPendingCompOffDaysCurrentYear(compOffPending);
    setApprovedSickDaysCurrentYear(sickApproved);
    setApprovedCasualDaysCurrentYear(casualApproved);
    setApprovedPaidDaysCurrentYear(Math.max(paidApproved, sickApproved + casualApproved));
    setTotalConsideredSickDaysCurrentYear(sickTotal);
    setTotalConsideredCasualDaysCurrentYear(casualTotal);
    setTotalConsideredPaidDaysCurrentYear(Math.max(paidTotal, sickTotal + casualTotal));
  };


  useEffect(() => {
    if (!organizationId || !userId) {
      return;
    }
    const loadLocalLeaves = async () => {
      setIsLoading(true);
      try {
        const orgDocRef = doc(db, `organization/${organizationId}`);

        const [orgSnap, limits] = await Promise.all([
          getDoc(orgDocRef),
          fetchLeaveLimits(organizationId, userId),
        ]);

        let tempLeaves: LeaveEntry[] = [];
        if (orgSnap.exists()) {
          const orgData = orgSnap.data() as any;
          const members: Member[] = orgData.members || [];
          const me = members.find((m) => m.id === userId);
          if (me) {
            tempLeaves = (me.tempLeaveRequests || []) as LeaveEntry[];
            // Avoid another fetch for joining date by setting it here if found
            const memberJoining = me.dateOfJoining || me.joiningDate || null;
            if (memberJoining) setJoiningDate(memberJoining);
          }
          // Fetch comp off enabled setting (default: true)
          setCompOffEnabled(orgData.compOffEnabled !== false);
        }
        const sortedDrafts = [...tempLeaves].sort((a, b) => {
          const dateA = new Date(a.fromDate).getTime();
          const dateB = new Date(b.fromDate).getTime();
          if (dateA !== dateB) return dateB - dateA;
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });
        setLeaveEntries(sortedDrafts);

        const thisYear = new Date().getFullYear();
        if (
          limits &&
          (limits.limitYear === undefined || limits.limitYear === thisYear)
        ) {
          setPaidLimit(limits.paidLimit ?? 8);
          setSickLimit(limits.sickLimit ?? 4);
          setCasualLimit(limits.casualLimit ?? 4);
        }

        // Parallelize remaining fetching tasks
        const isCompOffEnabled = orgSnap.exists() ? (orgSnap.data() as any).compOffEnabled !== false : true;
        const fetchTasks: Promise<void>[] = [
          fetchLeaveData(sortedDrafts)
        ];
        if (isCompOffEnabled) {
          fetchTasks.push(computeCompOffAvailability(organizationId, userId));
        }
        await Promise.all(fetchTasks);
      } finally {
        setIsLoading(false);
      }
    };
    loadLocalLeaves();
  }, [organizationId, userId]);

  useEffect(() => {
    if (!organizationId || !userId) {
      return;
    }
    const loadRejected = async () => {
      setIsLoading(true);
      try {
        const allEntries = await fetchAllLeaveEntries(organizationId, userId);
        const rej = allEntries
          .filter((e) => e.approvalStatus === "Rejected" || e.approvalStatus === "rejected")
          .map((e, idx) => ({ id: `${userId}-${idx}`, ...e }))
          .sort((a: any, b: any) => {
            const dateA = new Date(a.fromDate).getTime();
            const dateB = new Date(b.fromDate).getTime();
            if (dateA !== dateB) return dateB - dateA;
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          });
        setRejectedLeaves(rej);
      } finally {
        setIsLoading(false);
      }
    };
    loadRejected();
  }, [organizationId, userId]);

  useEffect(() => {
    if (!organizationId || !userId) {
      return;
    }
    const loadManagers = async () => {
      try {
        const orgDocRef = doc(db, `organization/${organizationId}`);
        const orgDocSnap = await getDoc(orgDocRef);
        if (orgDocSnap.exists()) {
          const orgData = orgDocSnap.data() as any;
          const allMembers: Manager[] = orgData.members || [];
          const managerList = allMembers.filter(
            (m) =>
              (isManagerialRole(m.role) || (m as any).approvalStatus === "Yes") &&
              m.id !== userId &&
              m.id !== authUser?.uid
          );
          setManagers(managerList);
        }
      } catch {
        // ignore
      }
    };
    loadManagers();
  }, [organizationId, userId]);

  const resetAddLeaveForm = () => {
    const d = new Date();
    setLeaveType("");
    setReason("");
    setFromDate(format(d, "yyyy-MM-dd"));
    setToDate(format(d, "yyyy-MM-dd"));
    setNumDays("");
    setSelectedManager("");
    setDayPortions({});
  };

  const validateFields = (
    lt: string,
    rs: string,
    fd: string,
    td: string,
    nd: string,
    mgr: string
  ) => {
    if (!lt) return "Please select a leave type.";
    if (!rs) return "Please select/enter a reason.";
    if (!fd) return "Please choose a From Date.";
    if (!td) return "Please choose a To Date.";
    if (!nd) return "Please enter the number of days.";
    if (!mgr) return "Please select a manager.";
    const days = Number(nd);
    if (isNaN(days) || days <= 0)
      return "Number of days must be greater than zero.";
    const start = new Date(fd);
    const end = new Date(td);
    if (start > end) return "From Date cannot be after To Date.";
    // Comp Off availability guard
    if (lt === "Paid" && rs === "Comp Off" && days > compOffAvailable) {
      return `Not enough Comp Off available. You have ${compOffAvailable}.`;
    }
    return "";
  };

  const addLeaveRequest = async () => {
    if (!organizationId || !userId) {
      showToast({ title: "Missing organizationId or userId", status: "error" });
      return;
    }
    const err = validateFields(
      leaveType,
      reason,
      fromDate,
      toDate,
      numDays,
      selectedManager
    );
    if (err) {
      showToast({ title: err, status: "warning" });
      return;
    }
    setIsAdding(true);
    const newEntry: LeaveEntry = {
      leaveType,
      reason,
      fromDate,
      toDate,
      numDays,
      approvalStatus: "Pending",
      timestamp: new Date().toISOString(),
      managerIds: [selectedManager],
      dayPortions: { ...dayPortions },
    };
    try {
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgDocSnap = await getDoc(orgDocRef);
      if (!orgDocSnap.exists()) {
        showToast({ title: "Organization doc not found.", status: "error" });
        return;
      }
      const data = orgDocSnap.data() as any;
      const members: Member[] = data.members || [];
      const idx = members.findIndex((m) => m.id === userId);
      if (idx === -1) {
        showToast({ title: "User not found in organization.", status: "error" });
        return;
      }
      const user = members[idx];
      const updatedTemp = user.tempLeaveRequests
        ? [...user.tempLeaveRequests, newEntry]
        : [newEntry];
      // Sort updated: FromDate desc, Timestamp desc
      const sorted = [...updatedTemp].sort((a, b) => {
        const dateA = new Date(a.fromDate).getTime();
        const dateB = new Date(b.fromDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      members[idx] = { ...user, tempLeaveRequests: sorted };
      await updateDoc(orgDocRef, { members });
      setLeaveEntries(sorted);
      resetAddLeaveForm();
      setModalVisible(false);
    } catch {
      showToast({ title: "Failed to add leave request.", status: "error" });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteLocal = async (index: number) => {
    if (!organizationId || !userId) return;
    if (!window.confirm("Delete this local leave item?")) return;
    try {
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgDocSnap = await getDoc(orgDocRef);
      if (!orgDocSnap.exists()) {
        showToast({ title: "Organization doc not found.", status: "error" });
        return;
      }
      const data = orgDocSnap.data() as any;
      const members: Member[] = data.members || [];
      const idx2 = members.findIndex((m) => m.id === userId);
      if (idx2 === -1) {
        showToast({ title: "User not found in organization.", status: "error" });
        return;
      }
      const user = members[idx2];
      const updatedTemp = [...(user.tempLeaveRequests || [])];
      updatedTemp.splice(index, 1);
      members[idx2] = { ...user, tempLeaveRequests: updatedTemp };
      await updateDoc(orgDocRef, { members });
      setLeaveEntries(updatedTemp);
    } catch {
      showToast({ title: "Failed to delete local leave.", status: "error" });
    }
  };

  const handleEditRejectedOpen = (
    entry: LeaveEntry & { id: string },
    index: number
  ) => {
    setEditSource("rejected");
    setEditIndex(index);
    setEditLeaveType(entry.leaveType || "");
    setEditReason(entry.reason || "");
    setEditFromDate(entry.fromDate || "");
    setEditToDate(entry.toDate || "");
    setEditNumDays(entry.numDays || "");
    setEditDayPortions({ ...(entry.dayPortions || {}) });
    setEditSelectedManager(
      entry.managerIds && entry.managerIds.length > 0 ? entry.managerIds[0] : ""
    );
    setEditModalVisible(true);
  };

  const handleEditDraftOpen = (entry: LeaveEntry, index: number) => {
    setEditSource("draft");
    setEditIndex(index);
    setEditLeaveType(entry.leaveType || "");
    setEditReason(entry.reason || "");
    setEditFromDate(entry.fromDate || "");
    setEditToDate(entry.toDate || "");
    setEditNumDays(entry.numDays || "");
    setEditDayPortions({ ...(entry.dayPortions || {}) });
    setEditSelectedManager(
      entry.managerIds && entry.managerIds.length > 0 ? entry.managerIds[0] : ""
    );
    setEditModalVisible(true);
  };

  const handleEditHistoryOpen = (entry: LeaveEntry, index: number) => {
    setEditSource("history");
    setEditIndex(index);
    setEditLeaveType(entry.leaveType || "");
    setEditReason(entry.reason || "");
    setEditFromDate(entry.fromDate || "");
    setEditToDate(entry.toDate || "");
    setEditNumDays(entry.numDays || "");
    setEditDayPortions({ ...(entry.dayPortions || {}) });
    setEditSelectedManager(
      entry.managerIds && entry.managerIds.length > 0 ? entry.managerIds[0] : ""
    );
    setEditModalVisible(true);
  };

  const handleSaveRejectedChanges = async () => {
    if (!organizationId || !userId) return;
    const err = validateFields(
      editLeaveType,
      editReason,
      editFromDate,
      editToDate,
      editNumDays,
      editSelectedManager
    );
    if (err) {
      showToast({ title: err, status: "warning" });
      return;
    }
    setIsEditSaving(true);
    try {
      const originalWithId = rejectedLeaves[editIndex as number];
      if (originalWithId._monthKey && originalWithId._entryId) {
        await deleteLeaveEntry(organizationId, userId, originalWithId);
      }

      const updatedItem: LeaveEntry = {
        leaveType: editLeaveType,
        reason: editReason,
        fromDate: editFromDate,
        toDate: editToDate,
        numDays: editNumDays,
        approvalStatus: "Pending",
        timestamp: new Date().toISOString(),
        userId,
        managerIds: [editSelectedManager],
        dayPortions: { ...editDayPortions },
      };

      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgDocRef);
      if (orgSnap.exists()) {
        const data = orgSnap.data() as any;
        const members: Member[] = data.members || [];
        const idx = members.findIndex((m) => m.id === userId);
        if (idx !== -1) {
          const user = members[idx];
          const newLocal = [...(user.tempLeaveRequests || []), updatedItem];
          // Sort updated: FromDate desc, Timestamp desc
          const sorted = [...newLocal].sort((a, b) => {
            const dateA = new Date(a.fromDate).getTime();
            const dateB = new Date(b.fromDate).getTime();
            if (dateA !== dateB) return dateB - dateA;
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          });
          members[idx] = { ...user, tempLeaveRequests: sorted };
          await updateDoc(orgDocRef, { members });
          setLeaveEntries(sorted);
        }
      }

      setRejectedLeaves((prev) =>
        prev.filter((_, i) => i !== (editIndex as number))
      );
      setEditModalVisible(false);
    } catch {
      showToast({ title: "Failed to edit rejected leave request.", status: "error" });
    } finally {
      setIsEditSaving(false);
    }
  };

  const handleSaveDraftChanges = async () => {
    if (!organizationId || !userId || editIndex === null) return;
    const err = validateFields(
      editLeaveType,
      editReason,
      editFromDate,
      editToDate,
      editNumDays,
      editSelectedManager
    );
    if (err) {
      showToast({ title: err, status: "warning" });
      return;
    }
    setIsEditSaving(true);
    try {
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgDocRef);
      if (orgSnap.exists()) {
        const data = orgSnap.data() as any;
        const members: Member[] = data.members || [];
        const idx = members.findIndex((m) => m.id === userId);
        if (idx !== -1) {
          const user = members[idx];
          const updatedLocal = [...(user.tempLeaveRequests || [])];
          updatedLocal[editIndex] = {
            leaveType: editLeaveType,
            reason: editReason,
            fromDate: editFromDate,
            toDate: editToDate,
            numDays: editNumDays,
            approvalStatus: "Pending",
            timestamp: new Date().toISOString(),
            userId,
            managerIds: [editSelectedManager],
            dayPortions: { ...editDayPortions },
          };
          members[idx] = { ...user, tempLeaveRequests: updatedLocal };
          await updateDoc(orgDocRef, { members });
          setLeaveEntries(updatedLocal);
        }
      }
      setEditModalVisible(false);
    } catch {
      showToast({ title: "Failed to edit draft leave request.", status: "error" });
    } finally {
      setIsEditSaving(false);
    }
  };

  const handleSaveHistoryChanges = async () => {
    if (!organizationId || !userId || editIndex === null) return;
    const err = validateFields(
      editLeaveType,
      editReason,
      editFromDate,
      editToDate,
      editNumDays,
      editSelectedManager
    );
    if (err) {
      showToast({ title: err, status: "warning" });
      return;
    }
    setIsEditSaving(true);
    try {
      const originalEntry = historyEntries[editIndex];
      if (originalEntry._monthKey && originalEntry._entryId) {
        await deleteLeaveEntry(organizationId, userId, originalEntry);

        const updatedItem: LeaveEntry = {
          ...originalEntry,
          leaveType: editLeaveType,
          reason: editReason,
          fromDate: editFromDate,
          toDate: editToDate,
          numDays: editNumDays,
          managerIds: [editSelectedManager],
          dayPortions: { ...editDayPortions },
        };
        await submitLeaveEntries(organizationId, userId, [updatedItem]);
      }

      setHistoryEntries((prev) =>
        prev.map((e, i) =>
          i === editIndex
            ? {
                ...e,
                leaveType: editLeaveType,
                reason: editReason,
                fromDate: editFromDate,
                toDate: editToDate,
                numDays: editNumDays,
                managerIds: [editSelectedManager],
                dayPortions: { ...editDayPortions },
              }
            : e
        )
      );
      setEditModalVisible(false);
    } catch {
      showToast({ title: "Failed to edit history leave request.", status: "error" });
    } finally {
      setIsEditSaving(false);
    }
  };

  const handleSaveEditChanges = async () => {
    if (editSource === "draft") return handleSaveDraftChanges();
    if (editSource === "history") return handleSaveHistoryChanges();
    return handleSaveRejectedChanges();
  };

  const handleDeleteRejected = async (index: number) => {
    if (!organizationId || !userId) return;
    if (!window.confirm("Delete this rejected leave?")) return;
    try {
      const uiEntry = rejectedLeaves[index];
      if (!uiEntry) return;
      await deleteLeaveEntry(organizationId, userId, uiEntry);
      setRejectedLeaves((prev) => prev.filter((_, i) => i !== index));
    } catch {
      showToast({ title: "Failed to delete rejected leave.", status: "error" });
    }
  };

  // Keep the per-day portion map in step with the selected range: dates that
  // are still in range keep their First/Second Half selection, new dates
  // default to a full day, and dropped dates are discarded.
  const syncPortionsToRange = (
    dates: string[],
    previous: Record<string, DayPortion>
  ): Record<string, DayPortion> => {
    const next: Record<string, DayPortion> = {};
    dates.forEach((d) => {
      next[d] = previous[d] || "full";
    });
    return next;
  };

  useEffect(() => {
    setDayPortions((prev) => syncPortionsToRange(addRangeDates, prev));
  }, [addRangeDates]);

  useEffect(() => {
    setEditDayPortions((prev) => syncPortionsToRange(editRangeDates, prev));
  }, [editRangeDates]);

  // No. of Days is derived from the range and its half-day selections.
  useEffect(() => {
    if (addRangeDates.length === 0) {
      setNumDays("");
      return;
    }
    setNumDays(
      formatNumDays(computeNumDaysFromPortions(addRangeDates, dayPortions))
    );
  }, [addRangeDates, dayPortions]);

  useEffect(() => {
    if (editRangeDates.length === 0) {
      setEditNumDays("");
      return;
    }
    setEditNumDays(
      formatNumDays(computeNumDaysFromPortions(editRangeDates, editDayPortions))
    );
  }, [editRangeDates, editDayPortions]);

  const handleSubmit = async () => {
    if (!organizationId || !userId) {
      showToast({ title: "Missing organizationId or userId", status: "error" });
      return;
    }
    setIsSaving(true);
    try {
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgDocSnap = await getDoc(orgDocRef);
      if (!orgDocSnap.exists()) {
        showToast({ title: "Organization doc not found.", status: "error" });
        setIsSaving(false);
        return;
      }

      const organizationData = orgDocSnap.data() as any;
      const members: Member[] = organizationData.members || [];
      const userIndex = members.findIndex((member) => member.id === userId);
      if (userIndex === -1) {
        showToast({ title: "User not found in organization.", status: "error" });
        setIsSaving(false);
        return;
      }

      const user = members[userIndex];
      const tempRequestsData = user.tempLeaveRequests || [];
      if (tempRequestsData.length === 0) {
        showToast({ title: "No leave requests to submit.", status: "warning" });
        setIsSaving(false);
        return;
      }

      await submitLeaveEntries(organizationId, userId, tempRequestsData.map((e) => ({ ...e, userId })));

      members[userIndex] = { ...user, tempLeaveRequests: [] };
      await updateDoc(orgDocRef, { members });
      setLeaveEntries([]);
      showToast({ title: "Leaves submitted successfully.", status: "success" });
      await fetchLeaveData([]);
    } catch {
      showToast({ title: "Failed to submit leaves.", status: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLockout) {
    return (
      <div className="px-4">
        <div className={`flex items-center justify-center py-20 ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} rounded-lg border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
          <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            You are not qualified to take leave as per company guidelines.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4">
      <style>{`
        input[type="date"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          opacity: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
        }
      `}</style>
      <div className="mb-4 w-full">
        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${compOffEnabled ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3 w-full`}>
          {/* Sick Leave */}
          <div className={`rounded-lg border p-4 text-center transition-colors ${isDark ? 'bg-gradient-to-b from-[#0F1216] to-[#1A1C1F] border-[#2E2F33] hover:border-[#E8C16C] text-white' : 'bg-gradient-to-b from-yellow-50/60 to-white border-yellow-200 hover:border-[#E8C16C] text-gray-800'}`}>
            <h3 className={`text-xs md:text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Sick Leave:{" "}
              <span className={`${isDark ? 'font-bold text-white' : 'font-bold text-gray-900'}`}>
                {summaryLoading ? (
                  <span className="inline-block animate-pulse">...</span>
                ) : (
                  effectiveTotals.sick
                )}
              </span>
            </h3>
            {summaryLoading ? (
              <div className="flex items-center justify-center py-3">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-400 border-t-transparent"></div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Taken</span>
                  <span className="font-semibold text-yellow-600">{approvedSickDaysCurrentYear}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Pending</span>
                  <span className="font-semibold text-amber-600">{pendingSickDaysCurrentYear}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Remaining</span>
                  <span className="font-semibold text-green-600">{sickRemainingApproved}</span>
                </div>
              </div>
            )}
          </div>

          {/* Casual Leave */}
          <div className={`rounded-lg border p-4 text-center transition-colors ${isDark ? 'bg-gradient-to-b from-[#0F1216] to-[#1A1C1F] border-[#2E2F33] hover:border-[#E8C16C] text-white' : 'bg-gradient-to-b from-teal-50/50 to-white border-teal-200 hover:border-[#E8C16C] text-gray-800'}`}>
            <h3 className={`text-xs md:text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Casual Leave:{" "}
              <span className={`${isDark ? 'font-bold text-white' : 'font-bold text-gray-900'}`}>
                {summaryLoading ? (
                  <span className="inline-block animate-pulse">...</span>
                ) : (
                  effectiveTotals.casual
                )}
              </span>
            </h3>
            {summaryLoading ? (
              <div className="flex items-center justify-center py-3">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-teal-400 border-t-transparent"></div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Taken</span>
                  <span className="font-semibold text-teal-600">{approvedCasualDaysCurrentYear}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Pending</span>
                  <span className="font-semibold text-amber-600">{pendingCasualDaysCurrentYear}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Remaining</span>
                  <span className="font-semibold text-green-600">{casualRemainingApproved}</span>
                </div>
              </div>
            )}
          </div>

          {/* Total Paid Leave */}
          <div className={`rounded-lg border p-4 text-center transition-colors ${isDark ? 'bg-gradient-to-b from-[#0F1216] to-[#1A1C1F] border-[#2E2F33] hover:border-[#E8C16C] text-white' : 'bg-gradient-to-b from-[#FFF7E6]/50 to-white border-[#E8C16C] hover:border-[#E8C16C] text-gray-800'}`}>
            <h3 className={`text-sm md:text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Total Paid Leave:{" "}
              <span className={`${isDark ? 'font-bold text-white' : 'font-bold text-gray-900'}`}>
                {summaryLoading ? (
                  <span className="inline-block animate-pulse">...</span>
                ) : (
                  effectiveTotals.paid
                )}
              </span>
            </h3>
            <div className="space-y-1">
              <div className="flex justify-between items-center text-xs">
                <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Taken</span>
                <span className="font-semibold text-[#B59143]">{approvedPaidDaysCurrentYear}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Pending</span>
                <span className="font-semibold text-amber-600">{pendingPaidDaysCurrentYear}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Remaining</span>
                <span className="font-semibold text-green-600">{Math.max(effectiveTotals.paid - approvedPaidDaysCurrentYear - pendingPaidDaysCurrentYear, 0)}</span>
              </div>
            </div>
          </div>

          {/* Unpaid Leave */}


          {/* Comp Off */}
          {compOffEnabled && (
          <div className={`rounded-lg border p-4 text-center transition-colors ${isDark ? 'bg-gradient-to-b from-[#0F1216] to-[#1A1C1F] border-[#2E2F33] hover:border-[#E8C16C] text-white' : 'bg-gradient-to-b from-rose-50/50 to-white border-rose-200 hover:border-[#E8C16C] text-gray-800'}`}>
            <h3 className={`text-sm md:text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Comp Off:{" "}
              <span className={`${isDark ? 'font-bold text-white' : 'font-bold text-gray-900'}`}>
                {compOffLoading ? (
                  <span className="inline-block animate-pulse">...</span>
                ) : (
                  compOffAvailable
                )}
              </span>
            </h3>
            {compOffLoading ? (
              <div className="flex items-center justify-center py-3">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-rose-400 border-t-transparent"></div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Available</span>
                  <span className={`${isDark ? 'font-semibold text-white' : 'font-semibold text-gray-800'}`}>{compOffAvailable}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Taken</span>
                  <span className={`${isDark ? 'font-semibold text-white' : 'font-semibold text-gray-800'}`}>{compOffTaken}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Pending</span>
                  <span className="font-semibold text-amber-600">{pendingCompOffDaysCurrentYear}</span>
                </div>
              </div>
            )}
          </div>
          )}
          <div className={`rounded-lg border p-4 text-center transition-colors ${isDark ? 'bg-gradient-to-b from-[#0F1216] to-[#1A1C1F] border-[#2E2F33] hover:border-[#E8C16C] text-white' : 'bg-gradient-to-b from-rose-50/50 to-white border-rose-200 hover:border-[#E8C16C] text-gray-800'}`}>
            <h3 className={`text-sm md:text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>Unpaid Leave</h3>
            <div className="space-y-1">
              <div className="flex justify-between items-center text-xs">
                <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Taken</span>
                <span className="font-semibold text-red-600">{leaveStats.unpaidTaken}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>Pending</span>
                <span className="font-semibold text-amber-600">{pendingUnpaidDaysCurrentYear}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-row items-end justify-between mb-6">

        <div className="flex-1 flex items-center gap-2">
          <h2 className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Leave Management
          </h2>

        </div>

        {/* Leave Statistics Display - Full Width Card */}

        <div className="w-full max-w-[200px] flex justify-end">
          <CustomDropdown
            value={selectedTab}
            onChange={(val: any) => setSelectedTab(val)}
            isDark={isDark}
            className="h-10 text-sm font-semibold rounded-md border-[#B59143]/40 w-full"
            options={[
              { value: "new", label: "Drafts" },
              { value: "rejected", label: "Rejected" },
              { value: "history", label: "History" }
            ]}
          />
        </div>
      </div>

      {selectedTab === "new" && (
        <>
          {!leaveEntries || leaveEntries.length === 0 ? (
            <div className={`flex flex-col items-center justify-center py-12 border border-dashed rounded-lg ${theme.card} ${theme.border}`}>
              <p className={`${theme.textMuted} mb-4`}>No leave requests added yet.</p>
              {leavePerms.canCreate && (
                <button
                  onClick={() => setModalVisible(true)}
                  className={`px-4 py-2 rounded-full text-sm focus:outline-none ${theme.primaryButton}`}
                >
                  Add Leave
                </button>
              )}
            </div>
          ) : (
            <div className={`max-h-[400px] overflow-y-auto overflow-x-auto ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} w-full border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'} rounded-md mb-4`}>
              <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      S.No
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Type
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Reason
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      From
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      To
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Days
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Manager
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? 'bg-[#1A1C1F] divide-y divide-[#2E2F33]' : 'bg-white divide-y divide-gray-200'}`}>
                  {leaveEntries.map((item, idx) => {
                    const serial = idx + 1;
                    return (
                      <tr
                        key={`${item.timestamp}-${idx}`}
                        className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}
                      >
                        <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {serial}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {item.leaveType}
                        </td>
                        <td className={`px-2 py-2 text-sm truncate max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {item.reason}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatDateDDMMYYYY(item.fromDate)}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatDateDDMMYYYY(item.toDate)}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <DaysCell entry={item} />
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {getManagerName(item.managerIds?.[0])}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div className="inline-flex items-center gap-2">
                            {leavePerms.canEdit(item) && (
                              <button
                                onClick={() =>
                                  handleEditDraftOpen(
                                    item,
                                    idx
                                  )
                                }
                                className={`inline-flex items-center p-1 rounded-full text-[#B59143] ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'} focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:ring-opacity-50`}
                                title="Edit"
                              >
                                <EditSvg />
                              </button>
                            )}
                            {leavePerms.canDelete(item) && (
                              <button
                                onClick={() =>
                                  handleDeleteLocal(
                                    idx
                                  )
                                }
                                className={`inline-flex items-center p-1 rounded-full text-rose-600 ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-rose-100'} focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-opacity-50`}
                                title="Delete"
                              >
                                <TrashSvg />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-4 justify-end mt-4">
            {leavePerms.canCreate && (
              <button
                onClick={() => setModalVisible(true)}
                className={`px-4 py-2 rounded-full text-sm focus:outline-none ${theme.primaryButton}`}
              >
                Add +
              </button>
            )}
            {leavePerms.canCreate && (
              <button
                onClick={handleSubmit}
                disabled={isSaving || leaveEntries.length === 0}
                className={`px-4 py-2 text-white rounded-full text-sm focus:outline-none ${isSaving || leaveEntries.length === 0 ? 'opacity-60 cursor-not-allowed' : ''
                  } ${theme.primaryButton}`}
              >
                {isSaving ? "Saving..." : "Submit"}
              </button>
            )}
          </div>
        </>
      )}

      {selectedTab === "rejected" && (
        <div>
          {rejectedLeaves.length === 0 ? (
            <p className="text-center text-gray-500">No rejected leaves.</p>
          ) : (
            <>
              <div className={`max-h-[400px] overflow-y-auto overflow-x-auto ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} w-full border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'} rounded-md`}>
                <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                  <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        S.No
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Type
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Reason
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        From
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        To
                      </th>
                      <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Days
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? 'bg-[#1A1C1F] divide-y divide-[#2E2F33]' : 'bg-white divide-y divide-gray-200'}`}>
                    {rejectedLeaves.map((entry, idx) => {
                      const serial =
                        idx + 1;
                      return (
                        <tr key={entry.id} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}>
                          <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {serial}
                          </td>
                          <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {entry.leaveType}
                          </td>
                          <td className={`px-2 py-2 text-sm truncate max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {entry.reason}
                          </td>
                          <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {formatDateDDMMYYYY(entry.fromDate)}
                          </td>
                          <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {formatDateDDMMYYYY(entry.toDate)}
                          </td>
                          <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <DaysCell entry={entry} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            </>
          )}
        </div>
      )}

      {selectedTab === "history" && (
        <div>
          {historyEntries.length === 0 ? (
            <div className={`flex flex-col items-center justify-center py-12 border border-dashed rounded-lg ${theme.card} ${theme.border}`}>
              <p className={`${theme.textMuted}`}>No submitted leave history found.</p>
            </div>
          ) : (
            <>
              <div className={`max-h-[320px] overflow-y-auto overflow-x-auto ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} w-full border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'} rounded-md`}>
                <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                  <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                    <tr>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        S.No
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Type
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Reason
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        From
                      </th>
                      <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        To
                      </th>
                      <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Days
                      </th>
                      <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Status
                      </th>
                      <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? 'bg-[#1A1C1F] divide-y divide-[#2E2F33]' : 'bg-white divide-y divide-gray-200'}`}>
                    {historyEntries.map((entry, idx) => {
                      const serial = idx + 1;
                      const status = entry.approvalStatus?.toLowerCase();

                      let statusClasses = "";
                      if (status === "approved" || status === "approve") statusClasses = "bg-green-100 text-green-700 border-green-200";
                      else if (status === "rejected") statusClasses = "bg-red-100 text-red-700 border-red-200";
                      else statusClasses = "bg-amber-100 text-amber-700 border-amber-200";

                      if (isDark) {
                        if (status === "approved" || status === "approve") statusClasses = "bg-green-500/10 text-green-400 border-green-500/20";
                        else if (status === "rejected") statusClasses = "bg-red-500/10 text-red-400 border-red-500/20";
                        else statusClasses = "bg-amber-500/10 text-amber-400 border-amber-500/20";
                      }

                      return (
                        <tr key={`${entry.timestamp}-${idx}`} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}>
                          <td className={`px-2 py-3 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {serial}
                          </td>
                          <td className={`px-2 py-3 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {entry.leaveType}
                          </td>
                          <td className={`px-2 py-3 text-sm truncate max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {entry.reason}
                          </td>
                          <td className={`px-2 py-3 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {formatDateDDMMYYYY(entry.fromDate)}
                          </td>
                          <td className={`px-2 py-3 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {formatDateDDMMYYYY(entry.toDate)}
                          </td>
                          <td className={`px-2 py-3 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <DaysCell entry={entry} />
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-center">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border uppercase tracking-wider ${statusClasses}`}>
                              {entry.approvalStatus}
                            </span>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-center">
                            {leavePerms.canEdit(entry) && (
                              <button
                                onClick={() =>
                                  handleEditHistoryOpen(
                                    entry,
                                    idx
                                  )
                                }
                                className={`inline-flex items-center p-1 rounded-full text-[#B59143] ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'} focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:ring-opacity-50`}
                                title="Edit"
                              >
                                <EditSvg />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            </>
          )}
        </div>
      )}

      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0B0F]/60 backdrop-blur-md animate-in fade-in duration-300">
          <div
            role="dialog"
            aria-modal="true"
            className={`relative w-full max-w-2xl ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white border-gray-200'
              } rounded-[28px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.25)] border overflow-hidden transform transition-all animate-in zoom-in-95 duration-300`}
          >
            {/* Top accent gradient line */}
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] opacity-90" />

            <div className="p-8 sm:p-10">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h3 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Add Leave Request
                  </h3>
                  <p className={`text-sm mt-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Fill the details to add a leave entry.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setModalVisible(false);
                    resetAddLeaveForm();
                  }}
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-200 ${isDark
                    ? 'border-[#2E2F33] text-gray-400 hover:bg-[#2A3441] hover:text-white'
                    : 'border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                    } focus:outline-none`}
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Leave Type */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Leave Type
                  </label>
                  <CustomDropdown
                    value={leaveType}
                    onChange={(value) => setLeaveType(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Leave Type" },
                      { value: "Paid", label: "Paid" },
                      { value: "Unpaid", label: "Unpaid" }
                    ]}
                  />
                </div>

                {/* Reason */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Reason
                  </label>
                  {leaveType === "Paid" ? (
                    <CustomDropdown
                      value={reason}
                      onChange={(value) => setReason(value)}
                      isDark={isDark}
                      className="w-full"
                      options={[
                        { value: "", label: "Select Reason" },
                        { value: "Sick", label: "Sick" },
                        { value: "Casual", label: "Casual" },
                        ...(compOffEnabled ? [{ value: "Comp Off", label: "Comp Off" }] : [])
                      ]}
                    />
                  ) : (
                    <div className="relative">
                      <input
                        className={`w-full rounded-md px-4 py-1.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none ${isDark
                          ? 'bg-[#0B0B0F] text-white border-[#2E2F33] focus:ring-2 focus:ring-[#E8C16C]/20 focus:border-[#E8C16C]/50'
                          : 'bg-white text-gray-900 border-gray-200 focus:ring-4 focus:ring-[#E8C16C]/10 focus:border-[#E8C16C]/50'
                          }`}
                        placeholder="Type reason here"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {/* From Date */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    FROM DATE
                  </label>
                  <div className="relative group cursor-pointer">
                    <div
                      className={`w-full flex items-center justify-between rounded-md px-4 py-2 text-xs border shadow-sm transition-all ${isDark
                        ? 'bg-[#0B0B0F] text-white border-[#2E2F33] hover:border-[#E8C16C]/50 focus:ring-2 focus:ring-[#E8C16C]/20'
                        : 'bg-white text-gray-900 border-gray-200 hover:border-[#E8C16C]/50 focus:ring-4 focus:ring-[#E8C16C]/10'
                        }`}
                      onClick={() => (document.getElementById('hiddenFromDate') as HTMLInputElement)?.showPicker()}
                    >
                      <span className={!fromDate ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}>
                        {fromDate ? formatDateDDMMYYYY(fromDate) : "dd-mm-yyyy"}
                      </span>
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-[#E8C16C] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      id="hiddenFromDate"
                      type="date"
                      className="absolute inset-0 opacity-0 pointer-events-none"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* To Date */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    TO DATE
                  </label>
                  <div className="relative group cursor-pointer">
                    <div
                      className={`w-full flex items-center justify-between rounded-md px-4 py-2 text-xs border shadow-sm transition-all ${isDark
                        ? 'bg-[#0B0B0F] text-white border-[#2E2F33] hover:border-[#E8C16C]/50 focus:ring-2 focus:ring-[#E8C16C]/20'
                        : 'bg-white text-gray-900 border-gray-200 hover:border-[#E8C16C]/50 focus:ring-4 focus:ring-[#E8C16C]/10'
                        }`}
                      onClick={() => (document.getElementById('hiddenToDate') as HTMLInputElement)?.showPicker()}
                    >
                      <span className={!toDate ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}>
                        {toDate ? formatDateDDMMYYYY(toDate) : "dd-mm-yyyy"}
                      </span>
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-[#E8C16C] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      id="hiddenToDate"
                      type="date"
                      className="absolute inset-0 opacity-0 pointer-events-none"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </div>

                {/* Day-wise duration */}
                <div className="space-y-1.5 md:col-span-2">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Duration Per Day
                  </label>
                  <DayPortionPicker
                    dates={addRangeDates}
                    portions={dayPortions}
                    onChange={(date, portion) =>
                      setDayPortions((prev) => ({ ...prev, [date]: portion }))
                    }
                    isDark={isDark}
                  />
                </div>

                {/* No. of Days */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    No. of Days
                  </label>
                  <input
                    readOnly
                    title="Calculated from the day-wise duration above"
                    className={`w-full rounded-md px-4 py-1.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none cursor-not-allowed ${isDark
                      ? 'bg-[#14161B] text-white border-[#2E2F33]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                      }`}
                    value={numDays}
                  />
                  {addNumDaysWarning && (
                    <p className="text-[11px] font-semibold text-rose-500 mt-1.5 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      {addNumDaysWarning}
                    </p>
                  )}
                </div>
                {/* Manager */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Manager
                  </label>
                  <CustomDropdown
                    value={selectedManager}
                    onChange={(value) => setSelectedManager(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Manager" },
                      ...managers.map((mgr) => ({
                        value: mgr.id,
                        label: `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim()
                      }))
                    ]}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 mt-10">
                <button
                  onClick={() => {
                    setModalVisible(false);
                    resetAddLeaveForm();
                  }}
                  className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 border ${isDark
                    ? 'border-[#2E2F33] text-gray-300 hover:bg-[#2A3441] hover:text-white'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  Cancel
                </button>
                <button
                  onClick={addLeaveRequest}
                  disabled={isAdding || !!addNumDaysWarning}
                  className={`px-8 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-[0.98] ${isAdding || !!addNumDaysWarning
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#E8C16C] to-[#B59143] hover:shadow-[0_8px_20px_-6px_rgba(181,145,67,0.5)]'
                    }`}
                >
                  {isAdding ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating...
                    </div>
                  ) : "Create Entry"}
                </button>
              </div>
            </div >
          </div >
        </div >
      )}

      {
        isEditModalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0B0F]/60 backdrop-blur-md animate-in fade-in duration-300">
            <div
              role="dialog"
              aria-modal="true"
              className={`relative w-full max-w-2xl ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white border-gray-200'
                } rounded-[28px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.25)] border overflow-hidden transform transition-all animate-in zoom-in-95 duration-300`}
            >
              {/* Top accent gradient line */}
              <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] opacity-90" />

              <div className="p-8 sm:p-10">
                <div className="flex items-start justify-between mb-8">
                  <div>
                    <h3 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {editSource === "draft" ? "Edit Draft Leave" : editSource === "history" ? "Edit Leave" : "Edit Rejected Leave"}
                    </h3>
                    <p className={`text-sm mt-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {editSource === "rejected" ? "Update the entry and resubmit for approval." : "Update the leave entry details."}
                    </p>
                  </div>
                  <button
                    onClick={() => setEditModalVisible(false)}
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-200 ${isDark
                      ? 'border-[#2E2F33] text-gray-400 hover:bg-[#2A3441] hover:text-white'
                      : 'border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                      } focus:outline-none`}
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Leave Type */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Leave Type
                    </label>
                    <CustomDropdown
                      value={editLeaveType}
                      onChange={(value) => setEditLeaveType(value)}
                      isDark={isDark}
                      className="w-full"
                      options={[
                        { value: "", label: "Select Leave Type" },
                        { value: "Paid", label: "Paid" },
                        { value: "Unpaid", label: "Unpaid" }
                      ]}
                    />
                  </div>

                  {/* Reason */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Reason
                    </label>
                    {editLeaveType === "Paid" ? (
                      <CustomDropdown
                        value={editReason}
                        onChange={(value) => setEditReason(value)}
                        isDark={isDark}
                        className="w-full"
                        options={[
                          { value: "", label: "Select Reason" },
                          { value: "Sick", label: "Sick" },
                          { value: "Casual", label: "Casual" },
                          ...(compOffEnabled ? [{ value: "Comp Off", label: "Comp Off" }] : [])
                        ]}
                      />
                    ) : (
                      <div className="relative">
                        <input
                          className={`w-full rounded-xl px-4 py-2.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none ${isDark
                            ? 'bg-[#0B0B0F] text-white border-[#2E2F33] focus:ring-2 focus:ring-[#E8C16C]/20 focus:border-[#E8C16C]/50'
                            : 'bg-white text-gray-900 border-gray-200 focus:ring-4 focus:ring-[#E8C16C]/10 focus:border-[#E8C16C]/50'
                            }`}
                          placeholder="Type reason here"
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                        />
                      </div>
                    )}
                  </div>

                  {/* From Date */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      FROM DATE
                    </label>
                    <div className="relative group cursor-pointer">
                      <div
                        className={`w-full flex items-center justify-between rounded-md px-4 py-2 text-xs border shadow-sm transition-all ${isDark
                          ? 'bg-[#0B0B0F] text-white border-[#2E2F33] hover:border-[#E8C16C]/50 focus:ring-2 focus:ring-[#E8C16C]/20'
                          : 'bg-white text-gray-900 border-gray-200 hover:border-[#E8C16C]/50 focus:ring-4 focus:ring-[#E8C16C]/10'
                          }`}
                        onClick={() => (document.getElementById('editHiddenFromDate') as HTMLInputElement)?.showPicker()}
                      >
                        <span className={!editFromDate ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}>
                          {editFromDate ? formatDateDDMMYYYY(editFromDate) : "dd-mm-yyyy"}
                        </span>
                        <svg className="w-4 h-4 text-gray-400 group-hover:text-[#E8C16C] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <input
                        id="editHiddenFromDate"
                        type="date"
                        className="absolute inset-0 opacity-0 pointer-events-none"
                        value={editFromDate}
                        onChange={(e) => setEditFromDate(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* To Date */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      TO DATE
                    </label>
                    <div className="relative group cursor-pointer">
                      <div
                        className={`w-full flex items-center justify-between rounded-md px-4 py-2 text-xs border shadow-sm transition-all ${isDark
                          ? 'bg-[#0B0B0F] text-white border-[#2E2F33] hover:border-[#E8C16C]/50 focus:ring-2 focus:ring-[#E8C16C]/20'
                          : 'bg-white text-gray-900 border-gray-200 hover:border-[#E8C16C]/50 focus:ring-4 focus:ring-[#E8C16C]/10'
                          }`}
                        onClick={() => (document.getElementById('editHiddenToDate') as HTMLInputElement)?.showPicker()}
                      >
                        <span className={!editToDate ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''}>
                          {editToDate ? formatDateDDMMYYYY(editToDate) : "dd-mm-yyyy"}
                        </span>
                        <svg className="w-4 h-4 text-gray-400 group-hover:text-[#E8C16C] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <input
                        id="editHiddenToDate"
                        type="date"
                        className="absolute inset-0 opacity-0 pointer-events-none"
                        value={editToDate}
                        onChange={(e) => setEditToDate(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Day-wise duration */}
                  <div className="space-y-1.5 md:col-span-2">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Duration Per Day
                    </label>
                    <DayPortionPicker
                      dates={editRangeDates}
                      portions={editDayPortions}
                      onChange={(date, portion) =>
                        setEditDayPortions((prev) => ({ ...prev, [date]: portion }))
                      }
                      isDark={isDark}
                    />
                  </div>

                  {/* No. of Days */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No. of Days
                    </label>
                    <input
                      readOnly
                      title="Calculated from the day-wise duration above"
                      className={`w-full rounded-xl px-4 py-2.5 text-sm placeholder-gray-500 transition-all border shadow-sm outline-none cursor-not-allowed ${isDark
                        ? 'bg-[#14161B] text-white border-[#2E2F33]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                        }`}
                      value={editNumDays}
                    />
                    {editNumDaysWarning && (
                      <p className="text-[11px] font-semibold text-rose-500 mt-1.5 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        {editNumDaysWarning}
                      </p>
                    )}
                  </div>
                  {/* Manager */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Manager
                    </label>
                    <CustomDropdown
                      value={editSelectedManager}
                      onChange={(value) => setEditSelectedManager(value)}
                      isDark={isDark}
                      className="w-full"
                      options={[
                        { value: "", label: "Select Manager" },
                        ...managers.map((mgr) => ({
                          value: mgr.id,
                          label: `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim()
                        }))
                      ]}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-4 mt-10">
                  <button
                    onClick={() => setEditModalVisible(false)}
                    className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 border ${isDark
                      ? 'border-[#2E2F33] text-gray-300 hover:bg-[#2A3441] hover:text-white'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEditChanges}
                    disabled={isEditSaving || !!editNumDaysWarning}
                    className={`px-8 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-[0.98] ${isEditSaving || !!editNumDaysWarning
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#E8C16C] to-[#B59143] hover:shadow-[0_8px_20px_-6px_rgba(181,145,67,0.5)]'
                      }`}
                  >
                    {isEditSaving ? (
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Saving...
                      </div>
                    ) : "Save Changes"}
                  </button>
                </div>
              </div >
            </div >
          </div >
        )
      }
    </div >
  );
};

export default LeaveApply;